"use client";

import { TypeImageCollectionGroup } from "@contentful/types";
import { notEmpty } from "@yardzen/js-util";
import { Button } from "@yardzen/ui/components/button";
import { ContentfulText } from "@yardzen/ui/components/contentful-text";
import { H3 } from "@yardzen/ui/components/h3";
import { Link } from "@yardzen/ui/components/link";
import { cn } from "@yardzen/ui/lib/utils";
import { cva } from "class-variance-authority";
import { ImageCollection } from "./ImageCollection";

export function ImageCollectionGroup({
  imageCollectionGroup,
}: {
  imageCollectionGroup: TypeImageCollectionGroup<"WITHOUT_UNRESOLVABLE_LINKS">;
}) {
  const { heading, textBlock, cta, imageCollections, numberOfDesktopColumns } =
    imageCollectionGroup.fields ?? {};
  const desktopColumnsResolved = numberOfDesktopColumns || 3;
  const gridVariants = cva("", {
    variants: {
      desktopColumns: {
        3: "md:grid-cols-3",
        4: "md:grid-cols-4",
      },
    },
  });

  return (
    <section className={cn("w-full px-8 py-16")}>
      <div className="mx-auto max-w-4xl">
        <div className="mb-6 text-center">
          <H3 className="font-display text-brand-secondary-dark pb-4 text-4xl">
            {heading}
          </H3>
          {textBlock && (
            <ContentfulText
              content={textBlock}
              className="text-brand-moss-greens-90 mx-auto mb-8 w-[90%] text-base leading-[140%]"
              pClassName="leading-[140%]"
            />
          )}
        </div>

        <div
          className={cn(
            "grid grid-cols-1 justify-center gap-8",
            gridVariants({
              desktopColumns: desktopColumnsResolved,
            }),
          )}
        >
          {imageCollections &&
            imageCollections
              .filter(notEmpty)
              .map((imageCollection, i) => (
                <ImageCollection
                  key={`imageCollection-${i}-${imageCollection.sys.id}`}
                  imageCollection={imageCollection}
                  variant="preview-small"
                />
              ))}
        </div>
        {cta && (
          <div className="my-6 text-center">
            <Button className="w-full sm:w-auto" asChild>
              <Link
                className="m-0 whitespace-nowrap font-medium max-[429px]:py-3 max-[390px]:px-3 max-[365px]:whitespace-normal max-[365px]:px-6 max-[365px]:leading-3 min-[360px]:inline-flex"
                href={cta?.fields.url}
              >
                {cta?.fields.buttonText}
              </Link>
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
